import { Input } from "../../ui/input";
import { useState, useEffect } from "react";
import axios from "axios";
import { Textarea } from "../../ui/textarea";
import { Button } from "../../ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../ui/select";
import { Loader2 } from "lucide-react";
import { STATUS_META, STATUS_ORDER } from "./prospectConstants";
import { CURRENCIES } from "../../../lib/money";

export default function ProspectFormDialog({ open, onOpenChange, editing, form, setForm, reps, onSave, saving }) {
  const [industries, setIndustries] = useState([]);
  useEffect(() => {
    if (!open) return;
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/lookups/industry_type`)
      .then((r) => setIndustries((r.data?.items || []).map((i) => i.label)))
      .catch(() => setIndustries([]));
  }, [open]);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#FFFFFF] border-[#DAD5CA] text-[#1A1A1A] max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="sales-form-dialog">
        <DialogHeader><DialogTitle>{editing ? "Edit Prospect" : "Add Prospect"}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <label className="text-xs text-[#59554D]">Company Name *</label>
            <Input value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })}
              className="bg-[#F4F2ED] border-[#DAD5CA] text-[#1A1A1A]" data-testid="sales-field-company" />
          </div>
          <div>
            <label className="text-xs text-[#59554D]">Industry</label>
            <select value={form.category || ""} onChange={(e) => setForm({ ...form, category: e.target.value })}
              className="w-full h-10 rounded-md border border-[#DAD5CA] bg-[#F4F2ED] px-2 text-[13px] text-[#1A1A1A]"
              data-testid="sales-field-category">
              <option value="">Not set</option>
              {form.category && !industries.includes(form.category) && (
                <option value={form.category}>{form.category}</option>
              )}
              {industries.map((i) => <option key={i} value={i}>{i}</option>)}
            </select>
            <p className="text-[11px] text-[#8A857A] mt-0.5">Carries across to the client record when they convert.</p>
          </div>
          <div>
            <label className="text-xs text-[#59554D]">Priority Tier</label>
            <Input value={form.priority_tier} onChange={(e) => setForm({ ...form, priority_tier: e.target.value })}
              placeholder="A / B / C" className="bg-[#F4F2ED] border-[#DAD5CA] text-[#1A1A1A]" data-testid="sales-field-tier" />
          </div>
          <div>
            <label className="text-xs text-[#59554D]">Clients in Network</label>
            <Input type="number" min="0" value={form.client_network_count}
              onChange={(e) => setForm({ ...form, client_network_count: e.target.value })}
              className="bg-[#F4F2ED] border-[#DAD5CA] text-[#1A1A1A]" data-testid="sales-field-network" />
          </div>
          <div>
            <label className="text-xs text-[#59554D]">Suburb</label>
            <Input value={form.suburb} onChange={(e) => setForm({ ...form, suburb: e.target.value })}
              className="bg-[#F4F2ED] border-[#DAD5CA] text-[#1A1A1A]" data-testid="sales-field-suburb" />
          </div>
          <div>
            <label className="text-xs text-[#59554D]">Country</label>
            <Input value={form.country ?? "Australia"} onChange={(e) => setForm({ ...form, country: e.target.value })}
              className="bg-[#F4F2ED] border-[#DAD5CA] text-[#1A1A1A]" data-testid="sales-field-country" />
          </div>
          <div>
            <label className="text-xs text-[#59554D]">Quote Currency</label>
            <select value={(form.currency || "AUD").toUpperCase()} onChange={(e) => setForm({ ...form, currency: e.target.value })}
              className="w-full h-10 rounded-md border border-[#DAD5CA] bg-[#F4F2ED] px-2 text-[13px] text-[#1A1A1A]" data-testid="sales-field-currency">
              {CURRENCIES.map((c) => <option key={c.code} value={c.code}>{c.label}</option>)}
            </select>
            <p className="text-[11px] text-[#8A857A] mt-0.5">{(form.currency || "AUD") === "AUD" ? "Quotes carry 10% GST." : "Quotes are tax-free exports — no tax collected."}</p>
          </div>
          <div>
            <label className="text-xs text-[#59554D]">Contact Name</label>
            <Input value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })}
              className="bg-[#F4F2ED] border-[#DAD5CA] text-[#1A1A1A]" data-testid="sales-field-contact" />
          </div>
          <div>
            <label className="text-xs text-[#59554D]">Contact Email</label>
            <Input value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })}
              className="bg-[#F4F2ED] border-[#DAD5CA] text-[#1A1A1A]" data-testid="sales-field-email" />
          </div>
          <div>
            <label className="text-xs text-[#59554D]">Contact Phone</label>
            <Input value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })}
              className="bg-[#F4F2ED] border-[#DAD5CA] text-[#1A1A1A]" data-testid="sales-field-phone" />
          </div>
          <div>
            <label className="text-xs text-[#59554D]">Source</label>
            <Input value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })}
              placeholder="Referral / Web / Event" className="bg-[#F4F2ED] border-[#DAD5CA] text-[#1A1A1A]" data-testid="sales-field-source" />
          </div>
          <div>
            <label className="text-xs text-[#59554D]">Assigned Rep</label>
            <Select value={form.assigned_rep || "unassigned"} onValueChange={(v) => setForm({ ...form, assigned_rep: v === "unassigned" ? "" : v })}>
              <SelectTrigger className="bg-[#F4F2ED] border-[#DAD5CA] text-[#1A1A1A]" data-testid="sales-field-rep"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#F4F2ED] border-[#DAD5CA]">
                <SelectItem value="unassigned">Unassigned</SelectItem>
                {reps.map(a => <SelectItem key={a.id} value={a.name}>{a.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs text-[#59554D]">Sequence Step</label>
            <Input value={form.current_sequence_step} onChange={(e) => setForm({ ...form, current_sequence_step: e.target.value })}
              placeholder="e.g. Step 1" className="bg-[#F4F2ED] border-[#DAD5CA] text-[#1A1A1A]" data-testid="sales-field-seq" />
          </div>
          <div>
            <label className="text-xs text-[#59554D]">Next Action Date</label>
            <Input type="date" value={form.next_action_date} onChange={(e) => setForm({ ...form, next_action_date: e.target.value })}
              className="bg-[#F4F2ED] border-[#DAD5CA] text-[#1A1A1A]" data-testid="sales-field-next" />
          </div>
          <div>
            <label className="text-xs text-[#59554D]">Status</label>
            <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
              <SelectTrigger className="bg-[#F4F2ED] border-[#DAD5CA] text-[#1A1A1A]" data-testid="sales-field-status"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#F4F2ED] border-[#DAD5CA]">
                {STATUS_ORDER.map(s => <SelectItem key={s} value={s}>{STATUS_META[s].label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-2">
            <label className="text-xs text-[#59554D]">Notes</label>
            <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
              className="bg-[#F4F2ED] border-[#DAD5CA] text-[#1A1A1A] min-h-[60px]" data-testid="sales-field-notes" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="text-[#59554D]">Cancel</Button>
          <Button onClick={onSave} disabled={saving} className="bg-[#0E6B3F] hover:bg-[#E3F3E9] text-black font-medium" data-testid="sales-form-save">
            {saving && <Loader2 className="w-4 h-4 animate-spin mr-1" />} {editing ? "Save Changes" : "Add Prospect"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
